import merge from 'lodash/merge';

import {
  componentParamsCollector,
  componentParamsEnricher,
  componentRequestParams,
} from 'strapi/component';
import { ReactComponents } from 'strapi/component/types';
import { pageRequestParams } from 'strapi/page';
import { pageComponents } from 'strapi/page/config';
import { PageType, PageTypes } from 'strapi/page/types';
import {
  NormalizedStrapiType,
  ReactComponentBuildResponse,
  RequestParams,
  StrapiComponentType,
  StrapiFilter,
  StrapiResponse,
} from 'strapi/types';

import MemoryCache from '../global/utils/memoryCache';

import { getSingle, getFields } from './client';
import { STRAPI_LANDING_PAGE_CACHE_KEY } from './constants';

// This is a Step 1 in create strapi page flow.
// Strapi backend returns by default only flat fields.
// To receive components, images, relations, we need to populate them, i.e.
// populate: {
//   image: true,
// }
// This function collects all the 'populate' objects for page and components and merges them together.
export const buildRequest = <T>(
  page: PageType,
  filter: StrapiFilter
): RequestParams<T> => {
  const requestParams = pageRequestParams[page](filter);
  if (!requestParams) {
    throw new Error(`Request params for type: ${page} not found`);
  }
  const components = pageComponents[page];
  if (!components) {
    throw new Error(`Components for type: ${page} not found`);
  }

  const request = components.reduce((request, component) => {
    return merge(request, componentRequestParams[component](page));
  }, {});

  merge(request, requestParams);

  return request;
};

// Strapi backend returns the response in format:
// {
//   data: [ { id: number, attributes: T } ],
//   meta: { pagination: Pagination }
// }
// and 'data' and 'attributes' can be on all nesting levels for all fields that were populated.
// We prefer to use types as we defined them in types.ts without strapi auxiliary fields.
// This function help us to remove all 'data', 'attributes' levels.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const normalize = <T extends Record<string, any>>(
  data: StrapiComponentType<T>
): NormalizedStrapiType<T> => {
  if (data === null || data === undefined) {
    return data;
  }

  if (Array.isArray(data)) {
    /* eslint-disable @typescript-eslint/no-explicit-any */
    return data.map((x) => normalize(x)) as any;
  }

  if (typeof data === 'object') {
    if ('data' in data) {
      /* eslint-disable @typescript-eslint/no-explicit-any */
      return normalize((data as any).data);
    }
    if ('attributes' in data) {
      /* eslint-disable @typescript-eslint/no-explicit-any */
      return normalize((data as any).attributes as any);
    }
    /* eslint-disable @typescript-eslint/no-explicit-any */
    for (const key in data as any) {
      data[key] = normalize(data[key]);
    }
  }

  return data;
};

// Next js do not accept undefined fields in getStaticProps response as it consider them unserializable.
// This function filters them out.
export const filterEmpty = <T extends object>(data: T): T => {
  return (Object.keys(data) as (keyof T)[])
    .filter((key) => data[key] !== undefined)
    .reduce((result, key) => {
      const value = data[key];

      if (value === undefined) {
        return result;
      }

      if (Array.isArray(value)) {
        const array = value
          .map((x) =>
            typeof x === 'object' && x !== null ? filterEmpty(x) : x
          )
          .filter((x) => x !== undefined);
        if (array && array.length) {
          // @ts-expect-error: types mismatch
          result[key] = array;
          return result;
        }
      }

      // @ts-expect-error: types mismatch
      result[key] =
        typeof value === 'object' && value !== null
          ? filterEmpty(value)
          : data[key];
      return result;
    }, {}) as T;
};

// This is a Step 2 in create strapi page flow.
// Most of the components need only data from the [populated] page type.
// However, if we need to fetch more data or adjust some data for the specific component,
// we can use this step.
export const enrichPageData = async (
  page: PageType,
  data: PageTypes
): Promise<PageTypes> => {
  const components = pageComponents[page];
  if (!components) {
    throw new Error(`Components for type ${page} not found`);
  }

  for (const component of components) {
    const enricher = componentParamsEnricher[component];
    if (enricher) {
      data = await enricher(page, data);
    }
  }

  return data;
};

// This is a Step 3 in create strapi page flow.
// All strapi pages consist of the same set of components.
// All components are stored on the root level of page.
// This method collects components props from the page data response for every known page
// in order specified in pageComponents.
export const buildPage = (
  page: PageType,
  data: PageTypes
): ReactComponentBuildResponse<ReactComponents>[] => {
  const components = pageComponents[page];
  if (!components) {
    throw new Error(`Components for type ${page} not found`);
  }

  return components
    .flatMap((component) => {
      const result = componentParamsCollector[component](page, data);
      if (result) {
        if (Array.isArray(result)) {
          return result.map((x) => ({
            component: x.component,
            props: filterEmpty(x.props),
          }));
        } else {
          return {
            component: result.component,
            props: filterEmpty(result.props),
          };
        }
      }
      return undefined;
    })
    .filter(
      (x) => x !== undefined && x !== null
    ) as ReactComponentBuildResponse<ReactComponents>[];
};

export const getStrapiLandingPageStaticProps = async <TPage extends PageTypes>(
  path: string,
  pageType: PageType
): Promise<
  ReactComponentBuildResponse<ReactComponents>[] | { notFound: boolean }
> => {
  const cacheKey = `${STRAPI_LANDING_PAGE_CACHE_KEY}${path}`;
  const cachedData = MemoryCache.get(
    cacheKey
  ) as ReactComponentBuildResponse<ReactComponents>[]; // Try to get the cached data using the cache key

  // If data exists in cache, return it instead of making a new request
  if (cachedData) {
    return cachedData;
  }

  const response = await getSingle<TPage>(pageType, {
    path: { $eq: path },
  });

  if ('error' in response) {
    console.error(response.error);
    return { notFound: true };
  }

  const enrichedResponse = await enrichPageData(pageType, response);

  const params = buildPage(pageType, enrichedResponse);

  // Cache the new data with the specified cache key
  MemoryCache.set(cacheKey, params);
  return params;
};

export const getPathsByPageType = async (page: PageType): Promise<string[]> => {
  const pages = await getFields(page, ['path']);

  if (!pages) {
    return [];
  }

  const pagesArray = Array.isArray(pages) ? pages : [pages];
  return pagesArray.map((x) => x.path).filter((x) => !!x);
};

export const getFirstStrapiDataFromArray = <T>(
  strapiResponse: StrapiResponse<T>
) => {
  if ('data' in strapiResponse) {
    // Not the cleanest, but when we render a page, we always expect one top level element, but the type
    // in Strapi can either be a single type or a multiple type. This is just a way to make the
    // result more uniform
    if (Array.isArray(strapiResponse.data)) {
      strapiResponse.data = strapiResponse.data[0];
    }
  }
  return strapiResponse;
};
